/**
 * SentryStatusSection — Sentry connection status and setup guide (extracted
 * verbatim from the bottom of the old god component).
 */
import { CheckCircle2, Copy, ExternalLink, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { trpc } from '@/lib/trpc';
import { SettingsSection } from '../components/console/SettingsSection';

export function SentryStatusSection() {
  const sentryStatusQuery = trpc.productionSettings.getSentryStatus.useQuery(undefined, {
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  // Frontend Sentry check
  const frontendDsn = import.meta.env.VITE_SENTRY_DSN;
  const frontendEnv = import.meta.env.VITE_SENTRY_ENVIRONMENT || 'development';
  const frontendConnected = Boolean(frontendDsn);

  // Backend Sentry check
  const backendStatus = sentryStatusQuery.data?.backend;
  const backendConnected = backendStatus?.enabled ?? false;
  const backendEnv = backendStatus?.environment ?? '-';

  const allConnected = frontendConnected && backendConnected;

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success('Copied to clipboard');
    });
  };

  return (
    <SettingsSection id="sentry" title="Error monitoring" icon={ShieldCheck}>
      <div className="space-y-6">
        {/* Connection Status */}
        <div className="space-y-3">
          <h4 className="text-sm font-medium">Connection Status</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* Backend */}
            <div className="flex items-center gap-3 p-3 rounded-lg border bg-card">
              <div className={`h-2.5 w-2.5 rounded-full shrink-0 ${backendConnected ? 'bg-emerald-500' : 'bg-red-500'}`} />
              <div className="min-w-0">
                <p className="text-sm font-medium">Backend</p>
                <p className="text-xs text-muted-foreground truncate">
                  {sentryStatusQuery.isLoading ? 'Checking...' : backendConnected ? `Connected (${backendEnv})` : 'Not connected'}
                </p>
              </div>
            </div>
            {/* Frontend */}
            <div className="flex items-center gap-3 p-3 rounded-lg border bg-card">
              <div className={`h-2.5 w-2.5 rounded-full shrink-0 ${frontendConnected ? 'bg-emerald-500' : 'bg-red-500'}`} />
              <div className="min-w-0">
                <p className="text-sm font-medium">Frontend</p>
                <p className="text-xs text-muted-foreground truncate">
                  {frontendConnected ? `Connected (${frontendEnv})` : 'Not connected'}
                </p>
              </div>
            </div>
          </div>

          {allConnected && (
            <div className="flex items-center gap-2 text-sm text-emerald-600 dark:text-emerald-400">
              <CheckCircle2 className="h-4 w-4" />
              Sentry is active. Errors, sync checkpoint alerts, and performance data are being tracked.
            </div>
          )}
        </div>

        {/* Setup Instructions */}
        {!allConnected && (
          <>
            <Separator />
            <div className="space-y-4">
              <h4 className="text-sm font-medium">Setup Instructions</h4>
              <p className="text-sm text-muted-foreground">
                Sentry captures errors, sync checkpoint stale alerts, and performance traces. Follow these steps to connect it:
              </p>

              <ol className="space-y-4 text-sm">
                <li className="space-y-2">
                  <p className="font-medium">1. Create a Sentry project</p>
                  <p className="text-muted-foreground">
                    Go to{' '}
                    <a href="https://sentry.io" target="_blank" rel="noopener noreferrer" className="text-primary underline inline-flex items-center gap-1">
                      sentry.io <ExternalLink className="h-3 w-3" />
                    </a>
                    {' '}and create two projects: one for <strong>Node.js</strong> (backend) and one for <strong>React</strong> (frontend). Copy the DSN from each project{"'"}s settings.
                  </p>
                </li>

                <li className="space-y-2">
                  <p className="font-medium">2. Set environment variables</p>
                  <p className="text-muted-foreground">Add these to your deployment environments:</p>

                  <div className="space-y-3">
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-xs font-medium text-muted-foreground">Railway (Backend)</p>
                        <Button variant="ghost" size="sm" className="h-6 gap-1 text-xs" onClick={() => copyToClipboard('SENTRY_DSN=\nSENTRY_ENVIRONMENT=production')}>
                          <Copy className="h-3 w-3" /> Copy
                        </Button>
                      </div>
                      <pre className="bg-muted p-3 rounded-md text-xs font-mono overflow-x-auto">
{`SENTRY_DSN=https://your-dsn@sentry.io/xxx
SENTRY_ENVIRONMENT=production`}
                      </pre>
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-xs font-medium text-muted-foreground">Vercel (Frontend)</p>
                        <Button variant="ghost" size="sm" className="h-6 gap-1 text-xs" onClick={() => copyToClipboard('VITE_SENTRY_DSN=\nVITE_SENTRY_ENVIRONMENT=production\nSENTRY_AUTH_TOKEN=')}>
                          <Copy className="h-3 w-3" /> Copy
                        </Button>
                      </div>
                      <pre className="bg-muted p-3 rounded-md text-xs font-mono overflow-x-auto">
{`VITE_SENTRY_DSN=https://your-dsn@sentry.io/xxx
VITE_SENTRY_ENVIRONMENT=production
SENTRY_AUTH_TOKEN=sntryu_xxx  (for source maps)`}
                      </pre>
                    </div>
                  </div>
                </li>

                <li className="space-y-2">
                  <p className="font-medium">3. Get your auth token (for source maps)</p>
                  <p className="text-muted-foreground">
                    In Sentry, go to <strong>Settings {'>'} Auth Tokens</strong> and create a token with <code className="bg-muted px-1 rounded text-xs">project:releases</code> and <code className="bg-muted px-1 rounded text-xs">org:read</code> scopes. Add it as <code className="bg-muted px-1 rounded text-xs">SENTRY_AUTH_TOKEN</code> in Vercel.
                  </p>
                </li>

                <li className="space-y-2">
                  <p className="font-medium">4. Redeploy</p>
                  <p className="text-muted-foreground">
                    After setting the environment variables, redeploy both backend (Railway) and frontend (Vercel). Come back here to verify the connection status shows green.
                  </p>
                </li>
              </ol>
            </div>
          </>
        )}

        {/* What Sentry Tracks */}
        <Separator />
        <div className="space-y-2">
          <h4 className="text-sm font-medium">What gets tracked</h4>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li className="flex items-center gap-2"><span className="h-1 w-1 rounded-full bg-muted-foreground shrink-0" /> Unhandled errors (frontend + backend)</li>
            <li className="flex items-center gap-2"><span className="h-1 w-1 rounded-full bg-muted-foreground shrink-0" /> Sync checkpoint stale alerts ({">"} 15 min without sync)</li>
            <li className="flex items-center gap-2"><span className="h-1 w-1 rounded-full bg-muted-foreground shrink-0" /> Order sync failures per order</li>
            <li className="flex items-center gap-2"><span className="h-1 w-1 rounded-full bg-muted-foreground shrink-0" /> API performance traces (10% sampling in production)</li>
            <li className="flex items-center gap-2"><span className="h-1 w-1 rounded-full bg-muted-foreground shrink-0" /> Frontend session replays on error (100% capture)</li>
          </ul>
        </div>
      </div>
    </SettingsSection>
  );
}
